import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Download, Send, Loader2, FileSignature, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TemplateCard = ({ tpl, users, courses, getAuthHeader }) => {
  const [userId, setUserId] = useState('');
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [courseId, setCourseId] = useState('');
  const [sending, setSending] = useState(false);

  const download = async () => {
    try {
      const q = courseId ? `?course_id=${encodeURIComponent(courseId)}` : '';
      const r = await axios.get(`${API}/admin/vorlagen/${tpl.id}/pdf${q}`, { ...getAuthHeader(), responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = tpl.filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download fehlgeschlagen'); }
  };

  const send = async () => {
    if (!userId && !email.trim()) { toast.error('Bitte Teilnehmer wählen oder E-Mail eingeben'); return; }
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/vorlagen/${tpl.id}/send`,
        { user_id: userId || null, email: email.trim() || null, note: note.trim() || null, course_id: courseId || null }, getAuthHeader());
      toast.success(r.data.message);
      setUserId(''); setEmail(''); setNote('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Versand fehlgeschlagen');
    } finally { setSending(false); }
  };

  return (
    <div className="bg-cream-50 border border-cream-300 rounded-2xl p-6" data-testid={`vorlage-card-${tpl.id}`}>
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-xl bg-espresso text-cream-50 flex items-center justify-center shrink-0">
          <FileSignature className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[9.5px] uppercase font-semibold tracking-[0.22em] text-terra">
            {tpl.group === 'nachweis' ? 'Nachweis-Liste · Blanko · PDF' : 'Blanko-Vorlage · PDF'}
          </p>
          <h3 className="font-display text-xl text-espresso mt-0.5">{tpl.title}</h3>
        </div>
        <button onClick={download} className="flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[11px] font-bold uppercase tracking-wider bg-espresso text-cream-50 hover:opacity-90" data-testid={`vorlage-download-${tpl.id}`}>
          <Download className="w-3.5 h-3.5" /> Herunterladen
        </button>
      </div>
      <div className="mt-5 pt-4 border-t border-cream-300">
        {tpl.group === 'vertrag' && (
          <>
            <p className="text-[11px] uppercase font-bold tracking-wider text-sand-500 mb-2.5">1. Weiterbildung wählen (wird ins Dokument eingetragen)</p>
            <select value={courseId} onChange={(e) => setCourseId(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-cream-400 bg-white text-[13px] text-espresso"
              data-testid={`vorlage-course-select-${tpl.id}`}>
              <option value="">Ohne Kursbezug (neutral)</option>
              {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
          </>
        )}
        <p className={`text-[11px] uppercase font-bold tracking-wider text-sand-500 mb-2.5 ${tpl.group === 'vertrag' ? 'mt-4' : ''}`}>
          {tpl.group === 'vertrag' ? '2. ' : ''}Per E-Mail versenden (oder oben herunterladen)
        </p>
        <div className="grid sm:grid-cols-2 gap-2.5">
          <select value={userId} onChange={(e) => { setUserId(e.target.value); if (e.target.value) setEmail(''); }}
            className="w-full px-3 py-2.5 rounded-lg border border-cream-400 bg-white text-[13px] text-espresso"
            data-testid={`vorlage-user-select-${tpl.id}`}>
            <option value="">Teilnehmer wählen…</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>{u.name || u.username}{u.email ? ` (${u.email})` : ' — keine E-Mail!'}</option>
            ))}
          </select>
          <input value={email} onChange={(e) => { setEmail(e.target.value); if (e.target.value) setUserId(''); }}
            placeholder="… oder E-Mail-Adresse eingeben"
            className="w-full px-3 py-2.5 rounded-lg border border-cream-400 bg-white text-[13px] text-espresso"
            data-testid={`vorlage-email-input-${tpl.id}`} />
        </div>
        <input value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="Optionale Anmerkung für die E-Mail (z.B. 'Bitte bis Freitag zurücksenden')"
          className="w-full mt-2.5 px-3 py-2.5 rounded-lg border border-cream-400 bg-white text-[13px] text-espresso"
          data-testid={`vorlage-note-input-${tpl.id}`} />
        <button onClick={send} disabled={sending}
          className="mt-3 flex items-center gap-1.5 px-4 py-2.5 rounded-full text-[11px] font-bold uppercase tracking-wider text-cream-50 disabled:opacity-60"
          style={{ backgroundColor: '#E07A5F' }}
          data-testid={`vorlage-send-${tpl.id}`}>
          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          {sending ? 'Wird gesendet…' : 'Als PDF-Anhang senden'}
        </button>
      </div>
    </div>
  );
};

const AI_COURSES = [
  { key: 'pdl1', label: 'PDL-Weiterbildung Teil 1: Grundlagen' },
  { key: 'pdl2', label: 'PDL-Weiterbildung Teil 2: Abrechnung & Dokumentation' },
  { key: 'geronto', label: 'Gerontopsychiatrische Fachweiterbildung' },
];

const AI_DOCS = [
  { docType: 'bewertung', title: 'Facharbeit-/Prüfungsbewertung', hint: 'Schriftlicher Teil mit 7 Kriterien + mündliche Prüfung mit Fragen und Gesamtnote — im Bereich „gut/befriedigend", Thema passend zum Kurs. Dauer: ~1 Minute.' },
  { docType: 'aufgaben', title: 'Aufgaben-Dokumentation (mit Antworten)', hint: 'Alle Einsendeaufgaben des Kurses mit KI-formulierten Antworten in Ich-Form, Abgabedaten über den Kurszeitraum verteilt. Dauer: je nach Kurs 3–10 Minuten.' },
];

const AttendanceDownloadCard = ({ users, courses, getAuthHeader }) => {
  const [courseId, setCourseId] = useState('');
  const [userId, setUserId] = useState('');
  const [downloading, setDownloading] = useState(false);

  const download = async () => {
    if (!courseId || !userId) { toast.error('Bitte Kurs und Teilnehmer wählen'); return; }
    setDownloading(true);
    try {
      const r = await axios.get(`${API}/admin/attendance-confirmation/${courseId}/${userId}`, { ...getAuthHeader(), responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'Teilnahmebescheinigung.pdf'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Download fehlgeschlagen');
    }
    setDownloading(false);
  };

  return (
    <div className="bg-cream-50 border border-cream-300 rounded-2xl p-6" data-testid="attendance-download-card">
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-xl bg-espresso text-cream-50 flex items-center justify-center shrink-0">
          <FileText className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[9.5px] uppercase font-semibold tracking-[0.22em] text-terra">Anlage zum Zertifikat · PDF</p>
          <h3 className="font-display text-xl text-espresso mt-0.5">Teilnahmebescheinigung inkl. Inhalte</h3>
          <p className="text-[12.5px] text-sand-600 mt-1">Bescheinigung mit vollständiger Modulliste, UE und Kurzbeschreibungen — ohne Status-Spalte, zum Versand zusätzlich zum manuell erstellten Zertifikat.</p>
        </div>
      </div>
      <div className="mt-5 pt-4 border-t border-cream-300 grid sm:grid-cols-3 gap-2.5">
        <select value={courseId} onChange={(e) => setCourseId(e.target.value)}
          className="w-full px-3 py-2.5 rounded-lg border border-cream-400 bg-white text-[13px] text-espresso"
          data-testid="attendance-course-select">
          <option value="">Kurs wählen…</option>
          {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
        </select>
        <select value={userId} onChange={(e) => setUserId(e.target.value)}
          className="w-full px-3 py-2.5 rounded-lg border border-cream-400 bg-white text-[13px] text-espresso"
          data-testid="attendance-user-select">
          <option value="">Teilnehmer wählen…</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.username}</option>)}
        </select>
        <button onClick={download} disabled={downloading}
          className="flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full text-[11px] font-bold uppercase tracking-wider text-cream-50 disabled:opacity-60"
          style={{ backgroundColor: '#E07A5F' }} data-testid="attendance-download-btn">
          {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
          Herunterladen
        </button>
      </div>
    </div>
  );
};

const ModullistenAdminCard = ({ users, courses, getAuthHeader }) => {
  const [entries, setEntries] = useState([]);
  const [requests, setRequests] = useState([]);
  const [reqCourseId, setReqCourseId] = useState('');
  const [reqUserId, setReqUserId] = useState('');
  const [sending, setSending] = useState(false);

  const load = () => {
    axios.get(`${API}/admin/modullisten`, getAuthHeader())
      .then((r) => setEntries(r.data.entries || [])).catch(() => {});
    axios.get(`${API}/admin/modulliste-requests`, getAuthHeader())
      .then((r) => setRequests(r.data.requests || [])).catch(() => {});
  };
  useEffect(load, []);

  const sendRequest = async () => {
    if (!reqCourseId || !reqUserId) { toast.error('Bitte Kurs und Teilnehmer wählen'); return; }
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/modulliste/request`,
        { user_id: reqUserId, course_id: reqCourseId }, getAuthHeader());
      toast.success(r.data.message);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Versand fehlgeschlagen');
    }
    setSending(false);
  };

  const download = async (entry) => {
    try {
      const r = await axios.get(`${API}/admin/modulliste/${entry.id}/pdf`, { ...getAuthHeader(), responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `Modulliste_${(entry.user_name || '').replace(/\s+/g, '_')}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download fehlgeschlagen'); }
  };

  return (
    <div className="bg-cream-50 border border-cream-300 rounded-2xl p-6" data-testid="modullisten-admin-card">
      <p className="font-mono text-[9.5px] uppercase font-semibold tracking-[0.22em] text-terra">Bearbeitungsnachweise</p>
      <h3 className="font-display text-xl text-espresso mt-0.5">Unterschriebene Modullisten</h3>
      <p className="text-[12.5px] text-sand-600 mt-1">Fordere Teilnehmer per E-Mail zur Unterschrift auf — nach dem Unterschreiben erscheint die Liste hier zum Download.</p>
      <div className="mt-4 grid sm:grid-cols-3 gap-2.5">
        <select value={reqCourseId} onChange={(e) => setReqCourseId(e.target.value)}
          className="w-full px-3 py-2.5 rounded-lg border border-cream-400 bg-white text-[13px] text-espresso"
          data-testid="modulliste-request-course">
          <option value="">Kurs wählen…</option>
          {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
        </select>
        <select value={reqUserId} onChange={(e) => setReqUserId(e.target.value)}
          className="w-full px-3 py-2.5 rounded-lg border border-cream-400 bg-white text-[13px] text-espresso"
          data-testid="modulliste-request-user">
          <option value="">Teilnehmer wählen…</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.username}</option>)}
        </select>
        <button onClick={sendRequest} disabled={sending}
          className="flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full text-[11px] font-bold uppercase tracking-wider text-cream-50 disabled:opacity-60"
          style={{ backgroundColor: '#E07A5F' }} data-testid="modulliste-request-send">
          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          Zur Unterschrift auffordern
        </button>
      </div>
      {requests.length > 0 && (
        <div className="mt-4 pt-3 border-t border-cream-300">
          <p className="font-mono text-[9px] uppercase font-semibold tracking-[0.2em] text-sand-600 mb-1.5">Angeforderte Unterschriften</p>
          {requests.map((r) => (
            <div key={r.id} className="flex items-center gap-3 py-1.5" data-testid={`modulliste-request-${r.id}`}>
              <span className={`inline-block w-2 h-2 rounded-full ${r.status === 'offen' ? 'bg-amber-500' : 'bg-emerald-600'}`} />
              <p className="flex-1 min-w-0 text-[12.5px] text-espresso truncate">
                {r.user_name} · {r.course_title}
                <span className="text-sand-600"> — {r.status === 'offen' ? `angefordert am ${new Date(r.requested_at).toLocaleDateString('de-DE')}` : 'unterschrieben ✓'}</span>
              </p>
            </div>
          ))}
        </div>
      )}
      {entries.length === 0 ? (
        <p className="text-[13px] text-sand-600 mt-2">Noch keine unterschriebenen Modullisten — Teilnehmer unterschreiben direkt auf ihrer Kursseite.</p>
      ) : (
        <div className="mt-4 divide-y divide-cream-300">
          {entries.map((e) => (
            <div key={e.id} className="flex items-center gap-3 py-2.5" data-testid={`modulliste-entry-${e.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-[13.5px] font-semibold text-espresso truncate">{e.user_name}</p>
                <p className="text-[12px] text-sand-600 truncate">{e.course_title} · {new Date(e.signed_at).toLocaleDateString('de-DE')}</p>
              </div>
              <button onClick={() => download(e)}
                className="flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[10.5px] font-bold uppercase tracking-wider border border-terra text-terra hover:bg-terra hover:text-cream-50 transition-colors"
                data-testid={`modulliste-admin-download-${e.id}`}>
                <Download className="w-3.5 h-3.5" /> PDF
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AiDocCard = ({ doc, users, getAuthHeader }) => {
  const [courseKey, setCourseKey] = useState('pdl1');
  const [userId, setUserId] = useState('');
  const [name, setName] = useState('');
  const [job, setJob] = useState(null);
  const [running, setRunning] = useState(false);
  const [faInfo, setFaInfo] = useState(null);
  const errCountRef = React.useRef(0);

  useEffect(() => {
    if (doc.docType !== 'bewertung' || !userId) { setFaInfo(null); return; }
    axios.get(`${API}/admin/pruefungsdoku/facharbeit-info/${courseKey}/${userId}`, getAuthHeader())
      .then((r) => setFaInfo(r.data))
      .catch(() => setFaInfo(null));
  }, [userId, courseKey, doc.docType]);

  const poll = async (jobId) => {
    try {
      const r = await axios.get(`${API}/admin/pruefungsdoku/jobs/${jobId}`, getAuthHeader());
      errCountRef.current = 0;
      setJob(r.data);
      if (r.data.status === 'done') {
        setRunning(false);
        const dl = await axios.get(`${API}/admin/pruefungsdoku/jobs/${jobId}/download`, { ...getAuthHeader(), responseType: 'blob' });
        const url = URL.createObjectURL(dl.data);
        const a = document.createElement('a');
        a.href = url; a.download = r.data.filename || 'Dokument.pdf'; a.click();
        URL.revokeObjectURL(url);
        toast.success('PDF fertig — Download gestartet');
      } else if (r.data.status === 'error') {
        setRunning(false);
        toast.error(`Generierung fehlgeschlagen: ${r.data.error || ''}`);
      } else {
        setTimeout(() => poll(jobId), 3000);
      }
    } catch (e) {
      // Vorübergehende Netzwerk-/Gateway-Fehler tolerieren, erst nach 8 Fehlversuchen aufgeben
      errCountRef.current += 1;
      if (e.response?.status === 404 || errCountRef.current >= 8) {
        setRunning(false);
        toast.error(e.response?.data?.detail || 'Status konnte nicht geladen werden — bitte erneut versuchen');
      } else {
        setTimeout(() => poll(jobId), 4000);
      }
    }
  };

  const start = async () => {
    if (!userId && !name.trim()) { toast.error('Bitte Teilnehmer wählen oder Namen eingeben'); return; }
    setRunning(true); setJob(null);
    try {
      const r = await axios.post(`${API}/admin/pruefungsdoku/generate`,
        { course_key: courseKey, doc_type: doc.docType, user_id: userId || null, participant_name: name.trim() || null },
        getAuthHeader());
      poll(r.data.job_id);
    } catch (e) {
      setRunning(false);
      toast.error(e.response?.data?.detail || 'Start fehlgeschlagen');
    }
  };

  return (
    <div className="bg-cream-50 border border-cream-300 rounded-2xl p-6" data-testid={`ki-doc-card-${doc.docType}`}>
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-xl text-cream-50 flex items-center justify-center shrink-0" style={{ backgroundColor: '#E07A5F' }}>
          <Sparkles className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[9.5px] uppercase font-semibold tracking-[0.22em] text-terra">KI-generiert · PDF</p>
          <h3 className="font-display text-xl text-espresso mt-0.5">{doc.title}</h3>
          <p className="text-[12.5px] text-sand-600 mt-1">{doc.hint}</p>
        </div>
      </div>
      <div className="mt-5 pt-4 border-t border-cream-300 grid sm:grid-cols-3 gap-2.5">
        <select value={courseKey} onChange={(e) => setCourseKey(e.target.value)}
          className="w-full px-3 py-2.5 rounded-lg border border-cream-400 bg-white text-[13px] text-espresso"
          data-testid={`ki-doc-course-${doc.docType}`}>
          {AI_COURSES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <select value={userId} onChange={(e) => { setUserId(e.target.value); if (e.target.value) setName(''); }}
          className="w-full px-3 py-2.5 rounded-lg border border-cream-400 bg-white text-[13px] text-espresso"
          data-testid={`ki-doc-user-${doc.docType}`}>
          <option value="">Teilnehmer wählen…</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.username}</option>)}
        </select>
        <input value={name} onChange={(e) => { setName(e.target.value); if (e.target.value) setUserId(''); }}
          placeholder="… oder Namen eingeben"
          className="w-full px-3 py-2.5 rounded-lg border border-cream-400 bg-white text-[13px] text-espresso"
          data-testid={`ki-doc-name-${doc.docType}`} />
      </div>
      {doc.docType === 'bewertung' && userId && faInfo && (
        <p className="mt-2 text-[12px]" style={{ color: faInfo.found ? '#2F7A3D' : '#8A8378' }} data-testid="ki-doc-facharbeit-info">
          {faInfo.found
            ? `✓ Hochgeladene Facharbeit gefunden („${faInfo.filename}") — die Bewertung bezieht sich auf den echten Inhalt`
            : 'Keine hochgeladene Facharbeit gefunden — Thema und Bewertung werden von der KI frei erstellt'}
        </p>
      )}
      <div className="mt-3 flex items-center gap-3">
        <button onClick={start} disabled={running}
          className="flex items-center gap-1.5 px-4 py-2.5 rounded-full text-[11px] font-bold uppercase tracking-wider text-cream-50 disabled:opacity-60"
          style={{ backgroundColor: '#E07A5F' }} data-testid={`ki-doc-generate-${doc.docType}`}>
          {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          {running ? 'Wird generiert…' : 'Mit KI generieren'}
        </button>
        {running && job && job.total > 0 && (
          <p className="text-[12px] text-sand-600" data-testid={`ki-doc-progress-${doc.docType}`}>
            Modul {job.progress || 0} / {job.total}{job.current ? ` · ${job.current}` : ''}
          </p>
        )}
      </div>
    </div>
  );
};

const SCHULUNGSPLAN_DOCS = [
  {
    file: '/docs/Schulungsplan_Behandlungspflege_LG1_LG2.pdf',
    title: 'Schulungsplan Weiterqualifikation Behandlungspflege (LG1 & LG2)',
    hint: '32 Unterrichtstage · 208 UE · Abschlussprüfung (schriftlich & mündlich) an Tag 32 — AZAV 364/1013/2026.',
    kicker: 'Schulungsplan · PDF',
  },
  {
    file: '/docs/Schulungsplan_Ambulanter_Pflegeassistent.pdf',
    title: 'Schulungsplan Qualifizierung Ambulanter Pflegeassistent inkl. LG1+LG2',
    hint: '46 Unterrichtstage · 300 UE · Abschlussprüfung (schriftlich & mündlich) an Tag 46 — AZAV 364/1045/2026.',
    kicker: 'Schulungsplan · PDF',
  },
  {
    file: '/docs/Schulungsplan_Pflegehilfskraft_mit_Betreuung.pdf',
    title: 'Schulungsplan Pflegehilfskraft & Betreuungsassistenz (§45b/§53b/§53c)',
    hint: '66 Unterrichtstage · 429 UE · Abschlussprüfung (schriftlich & mündlich) an Tag 66 — AZAV 364/1232/2024.',
    kicker: 'Schulungsplan · PDF',
  },
];

const PFA_DOCS = [
  {
    file: '/docs/Checkliste_Unterlagen_Externenpruefung_PFA.pdf',
    title: 'Checkliste: Unterlagen der Teilnehmenden',
    hint: 'Welche Dokumente Teilnehmende bis wann im Büro einreichen müssen — mit allen Fristen (NRW).',
    kicker: 'Externenprüfung PFA · PDF',
  },
  {
    file: '/docs/Vorlage_Arbeitgeberbescheinigung_PFA.pdf',
    title: 'Arbeitgeber-Bescheinigung (Vorlage)',
    hint: 'Ausfüllbare 3-Seiten-Vorlage für Betriebe: Zeiträume, Umfang, Tätigkeiten, Anleitung, Erklärung mit Stempel.',
    kicker: 'Externenprüfung PFA · PDF',
  },
  {
    file: '/docs/Praxiseinsatz_Nachweis_LG1-LG2.pdf',
    title: 'Praxiseinsatz-Nachweis LG1/LG2 (Checkliste)',
    hint: 'Tätigkeitskatalog zum Abhaken für den Praxiseinsatz (480 Std. / 3 Monate) — LG1-, LG2- und allgemeine Tätigkeiten, mit Feld für den Pflegedienst. Kein Vertrag, auch für Mitarbeiter geeignet.',
    kicker: 'Praxiseinsatz Behandlungspflege · PDF',
  },
  {
    file: '/docs/Praxiseinsatz_Nachweis_Ambulanter_Pflegeassistent.pdf',
    title: 'Praxiseinsatz-Nachweis Ambulanter Pflegeassistent (Checkliste)',
    hint: 'Tätigkeitskatalog zum Abhaken (480 Std. / 3 Monate) — Grundpflege/Tourbegleitung plus LG1-, LG2- und allgemeine Tätigkeiten, mit Feld für den Pflegedienst. Kein Vertrag, auch für Mitarbeiter geeignet.',
    kicker: 'Praxiseinsatz Ambulante Pflege · PDF',
  },
  {
    file: '/docs/Praktikumsvertrag_FB_TN_07.pdf',
    title: 'Praktikumsvertrag (FB_TN_07)',
    hint: 'Dreiseitige Vereinbarung nach AZAV § 4 / § 179 SGB III für das Praktikum der Pflegehilfskraft & Betreuungsassistenz — §§ 1-14 komplett.',
    kicker: 'Praktikum Pflegehilfskraft · PDF',
  },
  {
    file: '/docs/Laufzettel_Praktikum_Pflegehilfskraft.pdf',
    title: 'Praktikums-Laufzettel (FB_TN_08)',
    hint: 'Für das 5-Tage-Praktikum der Pflegehilfskraft & Betreuungsassistenz (AZAV 364/1232/2024) — Aufgabenliste mit Abzeichnung.',
    kicker: 'Praktikum Pflegehilfskraft · PDF',
  },
  {
    file: '/docs/Praktikums_Bewertung_FB_TN_09.pdf',
    title: 'Praktikums-Bewertung (FB_TN_09)',
    hint: 'Bewertungsbogen der Praxisanleitung (Anlage zu FB_TN_07): 5 Kompetenzbereiche mit Skala 1-5, Gesamtbewertung und Unterschriften.',
    kicker: 'Praktikum Pflegehilfskraft · PDF',
  },
];

const StaticDocCard = ({ doc }) => (
  <div className="bg-cream-50 border border-cream-300 rounded-2xl p-6" data-testid={`static-doc-${doc.file.split('/').pop()}`}>
    <div className="flex items-start gap-4">
      <div className="w-11 h-11 rounded-xl bg-espresso text-cream-50 flex items-center justify-center shrink-0">
        <FileText className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-mono text-[9.5px] uppercase font-semibold tracking-[0.22em] text-terra">{doc.kicker}</p>
        <h3 className="font-display text-xl text-espresso mt-0.5">{doc.title}</h3>
        <p className="text-[12.5px] text-sand-600 mt-1">{doc.hint}</p>
      </div>
      <a href={doc.file} target="_blank" rel="noreferrer"
        className="flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[11px] font-bold uppercase tracking-wider bg-espresso text-cream-50 hover:opacity-90 shrink-0"
        data-testid={`static-doc-download-${doc.file.split('/').pop()}`}>
        <Download className="w-3.5 h-3.5" /> Download
      </a>
    </div>
  </div>
);

export default function AdminVorlagenPage({ Layout }) {
  const { getAuthHeader } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/vorlagen`, getAuthHeader()),
      axios.get(`${API}/admin/users`, getAuthHeader()),
      axios.get(`${API}/courses`, getAuthHeader()),
    ]).then(([t, u, c]) => {
      setTemplates(t.data.templates || []);
      const list = (u.data || []).filter((x) => x.role !== 'admin' && !x.archived);
      setUsers(list);
      setCourses(c.data || []);
    }).catch(() => toast.error('Vorlagen konnten nicht geladen werden'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Layout>
      <div className="max-w-4xl mx-auto animate-fade-in" data-testid="admin-vorlagen-page">
      <p className="font-mono text-[10px] uppercase font-semibold tracking-[0.25em] text-terra mb-1">Admin · Dokumente</p>
      <h1 className="font-display text-4xl sm:text-5xl text-espresso leading-tight">Vorlagen</h1>
      <p className="font-display italic text-sand-600 mt-2 text-[15px]">
        Blanko-Verträge zum Herunterladen oder direkt per E-Mail an Teilnehmer senden — als ausfüllbares PDF im Anhang.
      </p>
      <div className="mt-8 space-y-5">
        {loading && <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-terra" /></div>}
        {!loading && templates.filter((t) => t.group === 'vertrag').map((tpl) => (
          <TemplateCard key={tpl.id} tpl={tpl} users={users} courses={courses} getAuthHeader={getAuthHeader} />
        ))}
        {!loading && templates.some((t) => t.group === 'nachweis') && (
          <div className="pt-4">
            <p className="font-mono text-[10px] uppercase font-semibold tracking-[0.25em] text-terra mb-1">Nachweis-Listen</p>
            <h2 className="font-display text-lg text-espresso">Was im Praktikum / in der Hospitation gemacht werden muss</h2>
            <p className="text-[13px] text-sand-600 mt-1">Kursspezifische Tätigkeitslisten zum Abhaken — von der anleitenden Fachkraft zu unterschreiben.</p>
          </div>
        )}
        {!loading && templates.filter((t) => t.group === 'nachweis').map((tpl) => (
          <TemplateCard key={tpl.id} tpl={tpl} users={users} courses={courses} getAuthHeader={getAuthHeader} />
        ))}
        {!loading && (
          <>
            <div className="pt-4">
              <p className="font-mono text-[10px] uppercase font-semibold tracking-[0.25em] text-terra mb-1">BG-Maßnahmen</p>
              <h2 className="font-display text-lg text-espresso">Schulungspläne der AZAV-Maßnahmen</h2>
              <p className="text-[13px] text-sand-600 mt-1">Tagesgenaue Übersicht aller Unterrichtstage — passend zum hinterlegten Tagesmaterial.</p>
            </div>
            {SCHULUNGSPLAN_DOCS.map((d) => <StaticDocCard key={d.file} doc={d} />)}
          </>
        )}
        {!loading && (
          <>
            <div className="pt-4">
              <p className="font-mono text-[10px] uppercase font-semibold tracking-[0.25em] text-terra mb-1">Externenprüfung PFA</p>
              <h2 className="font-display text-lg text-espresso">Zulassungsunterlagen für Teilnehmende & Betriebe</h2>
              <p className="text-[13px] text-sand-600 mt-1">Checkliste mit Fristen und die Arbeitgeber-Bescheinigung zum Weitergeben — auch im Kursbereich der Externenprüfung verfügbar.</p>
            </div>
            {PFA_DOCS.map((d) => <StaticDocCard key={d.file} doc={d} />)}
          </>
        )}
        {!loading && (
          <>
            <div className="pt-4">
              <p className="font-mono text-[10px] uppercase font-semibold tracking-[0.25em] text-terra mb-1">Prüfungsdokumente</p>
              <h2 className="font-display text-lg text-espresso">KI-generierte Nachweise für PDL & Gerontopsychiatrie</h2>
              <p className="text-[13px] text-sand-600 mt-1">Teilnehmer und Kurs wählen — die KI erstellt das fertige Dokument zum Ablegen im CRM/Teilnehmerportal.</p>
            </div>
            {AI_DOCS.map((doc) => (
              <AiDocCard key={doc.docType} doc={doc} users={users} getAuthHeader={getAuthHeader} />
            ))}
            <div className="pt-4">
              <p className="font-mono text-[10px] uppercase font-semibold tracking-[0.25em] text-terra mb-1">Teilnehmer-Nachweise</p>
              <h2 className="font-display text-lg text-espresso">Bescheinigungen & Modullisten</h2>
            </div>
            <AttendanceDownloadCard users={users} courses={courses} getAuthHeader={getAuthHeader} />
            <ModullistenAdminCard users={users} courses={courses} getAuthHeader={getAuthHeader} />
          </>
        )}
      </div>
      <p className="mt-6 text-[11.5px] text-sand-500 flex items-center gap-1.5">
        <FileText className="w-3.5 h-3.5" /> Hinweis: Die Vorlagen sind sorgfältig erstellt, ersetzen aber keine Rechtsberatung.
      </p>
      </div>
    </Layout>
  );
}
